<template>
  <div class="monopoly-page">
    <div class="monopoly-inner">
      <ActLang />
      <ActCountDown />
      <MainHeader />
      <GameBoard />
      <ExtractRewards />
      <CircleRewards />
      <Ranking />
      <img class="footer-img" src="@/views/act/monopoly/assets/img/footer-img.png" alt="footer-img">
      <DailyDiceDialog
        v-model="dailyDiceDialogVisible"
        :claimed-total="activityInfo.dailyDiceClaimedTotal"
        :max-total="DAILY_DICE_MAX_TOTAL"
        :claiming="claimingDailyDice"
        @claim="onClaimDailyDice"
      />
      <LoginDialog
        v-model="loginDialogVisible"
        @loginSuccess="onLoginSuccess"
      />
      <ActTimeDialog
        v-model="actTimeDialogVisible"
        :type="actTimeDialogType"
        :start-ms="actTimeStartMs"
        :server-ms="actTimeServerMs"
        @countdown-end="onActStartCountdownEnd"
      />
    </div>
    <AppFooter />
    <ActFloat />
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { provide, reactive, ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { toastService } from '@/utils/toastService'
import ActLang from './components/ActLang/index.vue'
import ActCountDown from './components/ActCountDown/index.vue'
import MainHeader from './components/MainHeader/index.vue'
import GameBoard from './components/GameBoard/index.vue'
import DailyDiceDialog from './components/DailyDiceDialog/index.vue'
import LoginDialog from './components/LoginDialog/index.vue'
import ActTimeDialog from './components/ActTimeDialog/index.vue'
import MonopolyApi from './api'
import ExtractRewards from './components/ExtractRewards/index.vue'
import CircleRewards from './components/CircleRewards/index.vue'
import Ranking from './components/Ranking/index.vue'
import ActFloat from './components/ActFloat/index.vue'
import AppFooter from './components/AppFooter/index.vue'
import {
  markPageSourceFromQuery,
  trackPage,
  trackPageExit,
  trackGetDice,
  DICE_NORMAL,
} from './utils/track'

const DAILY_DICE_MAX_TOTAL = 3

const route = useRoute()
const router = useRouter()
const store = useStore()
const { t } = useI18n()

const commonConfig = reactive({
  activityStartTime: '',
  rechargeStatStartTime: '',
  rechargeStatEndTime: '',
  activityPlayEndTime: '',
  activityRewardEndTime: '',
  activityRankSettleTime: '',
  activityPageCloseTime: '',
  boardCells: [],
  lapRewards: [],
  rankRewards: [],
  poolRewards: {
    normal: [],
    lucky: [],
    mystery: [],
  },
  serverTime: 0,
})

const activityInfo = reactive({
  cpUserId: '',
  cpNickName: '',
  normalDiceCount: 0,
  universalDiceCount: 0,
  position: 0,
  totalLaps: 0,
  lapReachTime: 0,
  dailyDiceStatus: 0,
  dailyDiceClaimedTotal: 0,
  pendingRewardCount: 0,
  lapRewardRedDot: false,
  extractRedDot: false,
  mysteryHit: false,
  activityPhase: '',
  serverTime: 0,
  totalDiamond: 0,
})

provide('monopolyCommonConfig', commonConfig)
provide('monopolyActivityInfo', activityInfo)

let rankListRefresher = async () => {}
const refreshRankList = () => rankListRefresher()
provide('registerMonopolyRankListRefresh', (fn) => {
  rankListRefresher = typeof fn === 'function' ? fn : (async () => {})
})
provide('refreshMonopolyRankList', refreshRankList)

let lapRewardListRefresher = async () => {}
const refreshLapRewardList = () => lapRewardListRefresher()
provide('registerMonopolyLapRewardListRefresh', (fn) => {
  lapRewardListRefresher = typeof fn === 'function' ? fn : (async () => {})
})
provide('refreshMonopolyLapRewardList', refreshLapRewardList)

const dailyDiceDialogVisible = ref(false)
const claimingDailyDice = ref(false)
const loginDialogVisible = ref(false)
const actTimeDialogVisible = ref(false)
const actTimeDialogType = ref('not-start') // not-start | ended
let actStartRefreshTimer = null

const toMs = (value) => {
  if (value == null || value === '') return NaN
  if (typeof value === 'number') {
    return value > 1e12 ? value : value * 1000
  }
  const text = String(value).trim()
  if (/^\d+$/.test(text)) {
    const num = Number(text)
    return num > 1e12 ? num : num * 1000
  }
  const parsed = Date.parse(text)
  return Number.isNaN(parsed) ? NaN : parsed
}

const actTimeStartMs = computed(() => {
  const ms = toMs(commonConfig.activityStartTime)
  return Number.isNaN(ms) ? 0 : ms
})

const actTimeServerMs = computed(() => {
  const ms = toMs(commonConfig.serverTime)
  return Number.isNaN(ms) ? 0 : ms
})

const syncActTimeDialog = () => {
  const startMs = actTimeStartMs.value
  const closeMs = toMs(commonConfig.activityPageCloseTime)
  const serverMs = actTimeServerMs.value
  if (!serverMs) {
    actTimeDialogVisible.value = false
    return
  }
  // 距开始不足 1s 时不再弹未开始（避免倒计时结束刷新后因误差再次弹出 0:00:00）
  if (startMs && serverMs < startMs && startMs - serverMs > 1000) {
    actTimeDialogType.value = 'not-start'
    actTimeDialogVisible.value = true
    dailyDiceDialogVisible.value = false
    return
  }
  if (!Number.isNaN(closeMs) && serverMs >= closeMs) {
    actTimeDialogType.value = 'ended'
    actTimeDialogVisible.value = true
    dailyDiceDialogVisible.value = false
    return
  }
  actTimeDialogVisible.value = false
}

const openMonopolyLogin = () => {
  loginDialogVisible.value = true
}
provide('openMonopolyLogin', openMonopolyLogin)

const resetActivityInfo = () => {
  Object.assign(activityInfo, {
    cpUserId: '',
    cpNickName: '',
    normalDiceCount: 0,
    universalDiceCount: 0,
    position: 0,
    totalLaps: 0,
    lapReachTime: 0,
    dailyDiceStatus: 0,
    dailyDiceClaimedTotal: 0,
    pendingRewardCount: 0,
    lapRewardRedDot: false,
    extractRedDot: false,
    mysteryHit: false,
    activityPhase: '',
    serverTime: 0,
    totalDiamond: 0,
  })
  dailyDiceDialogVisible.value = false
}

const loadCommonConfig = async () => {
  try {
    const res = await MonopolyApi.getCommonConfig()
    if (res) {
      Object.assign(commonConfig, res)
      syncActTimeDialog()
    }
  } catch (e) {
    console.error(e)
  }
}

// dailyDiceStatus: 0不可领 1可领 2已领 —— 可领时弹窗，点领取再请求
const openDailyDiceDialogIfNeeded = () => {
  if (!store.state.actUserInfo?.token) return
  if (actTimeDialogVisible.value) return
  if (Number(activityInfo.dailyDiceStatus) !== 1) return
  // 等 loading 收起后再弹，避免被遮罩挡住
  nextTick(() => {
    if (actTimeDialogVisible.value) return
    dailyDiceDialogVisible.value = true
  })
}

const loadActivityInfo = async () => {
  if (!store.state.actUserInfo?.token) {
    resetActivityInfo()
    return null
  }
  try {
    const res = await MonopolyApi.getActivityInfo()
    if (res) {
      Object.assign(activityInfo, res)
      openDailyDiceDialogIfNeeded()
    }
    return res
  } catch (e) {
    console.error(e)
    return null
  }
}

provide('refreshMonopolyActivityInfo', loadActivityInfo)

const initLogin = async () => {
  markPageSourceFromQuery(route.query)
  const tokenHash = route.query?.tokenHash
  const uid = route.query?.uid
  if (tokenHash && uid) {
    router.replace({
      query: {
        ...route.query,
        uid: undefined,
        tokenHash: undefined,
      },
    })
    // 内部会上报 act_monopoly_login 并整页跳转
    await MonopolyApi.gameTokenLogin({
      uid,
      tokenHash,
    })
    return true
  }
  return false
}

const onLoginSuccess = () => {
  loadActivityInfo()
}

watch(
  () => store.state.actUserInfo?.token,
  (token) => {
    if (token) {
      loadActivityInfo()
    } else {
      resetActivityInfo()
    }
  }
)

const init = async () => {
  const willReload = await initLogin()
  if (willReload) return
  trackPage()
  await loadCommonConfig()
  if (store.state.actUserInfo?.token) {
    await loadActivityInfo()
  }
}

const refreshPageData = async () => {
  await Promise.all([
    loadCommonConfig(),
    refreshRankList(),
    store.state.actUserInfo?.token ? loadActivityInfo() : Promise.resolve(null),
  ])
}

const onActStartCountdownEnd = () => {
  // 倒计时到点后稍等 1s，避免本地倒计时与服务器时间有误差导致又弹未开始
  if (actStartRefreshTimer) {
    clearTimeout(actStartRefreshTimer)
    actStartRefreshTimer = null
  }
  actStartRefreshTimer = setTimeout(() => {
    actStartRefreshTimer = null
    refreshPageData()
  }, 1000)
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    trackPageExit()
  } else {
    refreshPageData()
  }
}

const handlePageHide = () => {
  trackPageExit()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)
})

onUnmounted(() => {
  trackPageExit()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pagehide', handlePageHide)
  if (actStartRefreshTimer) {
    clearTimeout(actStartRefreshTimer)
    actStartRefreshTimer = null
  }
})

const onClaimDailyDice = async () => {
  if (!store.state.actUserInfo?.token) return
  if (claimingDailyDice.value) return

  claimingDailyDice.value = true
  try {
    const res = await MonopolyApi.claimDailyDice()
    if (res) {
      activityInfo.normalDiceCount = res.normalDiceCount
      activityInfo.dailyDiceClaimedTotal = res.dailyDiceClaimedTotal
      activityInfo.dailyDiceStatus = 2
      dailyDiceDialogVisible.value = false
      trackGetDice({ source: 1, diceAmount: 1, diceType: DICE_NORMAL })
      toastService.show(t('monopoly.dailyDiceClaimSuccess'))
    }
  } catch (e) {
    console.error(e)
    try {
      const info = await MonopolyApi.getActivityInfo()
      if (info) Object.assign(activityInfo, info)
    } catch (err) {
      console.error(err)
    }
    if (activityInfo.dailyDiceStatus !== 1) {
      dailyDiceDialogVisible.value = false
    }
  } finally {
    claimingDailyDice.value = false
  }
}

init()
</script>

<style lang="less" scoped>
.monopoly-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: normal;
  word-break: normal;
}

.monopoly-inner {
  width: 750px;
  max-width: 100%;
  position: relative;
  overflow-x: hidden;
  background-image: url("~@/views/act/monopoly/assets/img/bg.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top center;
  padding-bottom: 100px;
}
.footer-img {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 750px;
  height: 183px;
}
</style>
